import { parseDate, stringifyDate } from "../utils/dateInput"
import { baseApi } from "./api"

const plotsApi = baseApi.enhanceEndpoints({ addTagTypes: ["Plots", "Searches"] }).injectEndpoints({
    endpoints: (builder) => ({
        getPlots: builder.query({
            query: () => 'plot',
            // Provides a list of `Plots` by `id`.
            // If any mutation is executed that `invalidate`s any of these tags, this query will re-run to be always up-to-date.
            // The `LIST` id is a "virtual id" we just made up to be able to invalidate this query specifically if a new `Plots` element was added.
            providesTags: (result) =>
                // is result available?
                result
                    ? // successful query
                    [
                        ...result.map(({ _id }) => ({ type: 'Plots', id: _id })),
                        { type: 'Plots', id: 'LIST' },
                    ]
                    : // an error occurred, but we still want to refetch this query when `{ type: 'Plots', id: 'LIST' }` is invalidated
                    [{ type: 'Plots', id: 'LIST' }],
        }),
        addPlot: builder.mutation({
            query(body) {
                return {
                    url: `plot`,
                    method: 'POST',
                    body,
                }
            },
            // Invalidates all Plot-type queries providing the `LIST` id - after all, depending of the sort order,
            // that newly created plot could show up in any lists.
            invalidatesTags: [{ type: 'Plots', id: 'LIST' },{ type: 'Searches', id: 'ALL'}],
        }),
        getPlot: builder.query({
            query: (_id) => `plot/${_id}`,
            transformResponse: (data) => {
                if(data && data.date){
                    data = {...data, ...parseDate(data.date)};
                }
                return data;
            },
            providesTags: (result, error, arg) => error?[]:[{ type: 'Plots', id: arg }],
        }),
        updatePlot: builder.mutation({
            query(data) {
                const { _id, ...body } = data;
                return {
                    url: `plot/${_id}`,
                    method: 'PUT',
                    body,
                }
            },
            // Invalidates all queries that subscribe to this Plot `id` only.
            // In this case, `getPlot` will be re-run. `getPlots` *might*  rerun, if this id was under its results.
            invalidatesTags: (result, error, { _id }) => error?[]:[{ type: 'Plots', id: _id },{ type: 'Searches', id: 'ALL'}],
        }),
        deletePlot: builder.mutation({
            query(_id) {
                return {
                    url: `plot/${_id}`,
                    method: 'DELETE',
                }
            },
            // Invalidates all queries that subscribe to this Plot `id` only.
            invalidatesTags: (result, error, arg) => [{ type: 'Plots', id: arg }, { type: 'Searches', id: 'ALL'}],
        }),
        addPlotOwner: builder.mutation({
            query(data) {
                const { _id, ...body } = data
                return {
                    url: `plot/${_id}/owner`,
                    method: 'POST',
                    body,
                }
            },
            // Invalidates all queries that subscribe to this Plot `id` only.
            // In this case, `getPlot` will be re-run. `getPlots` *might*  rerun, if this id was under its results.
            invalidatesTags: (result, error, { _id }) => error?[]:[{ type: 'Plots', id: result.plotId }, { type: 'Searches', id: 'ALL'}],
        }),
        updatePlotOwner: builder.mutation({
            query(data) {
                const { _id, ...body } = data

                return {
                    url: `plot/owner/${_id}`,
                    method: 'PUT',
                    body,
                }
            },
            // Invalidates all queries that subscribe to this Plot `id` only.
            // In this case, `getPlot` will be re-run. `getPlots` *might*  rerun, if this id was under its results.
            invalidatesTags: (result, error, { _id }) => error?[]:[{ type: 'Plots', id: result.plotId }, { type: 'Searches', id: 'ALL'}],
        }),
        deletePlotOwner: builder.mutation({
            query(_id) {
                return {
                    url: `plot/owner/${_id}`,
                    method: 'DELETE',
                }
            },
            // Invalidates all queries that subscribe to this Plot `id` only.
            invalidatesTags: (result, error, arg) => error?[]:[{ type: 'Plots', id: result.plotId }, { type: 'Searches', id: 'ALL'}],
        }),
        addPlotOccupant: builder.mutation({
            query(data) {
                const { _id, ...body } = data
                return {
                    url: `plot/${_id}/occupant`,
                    method: 'POST',
                    body,
                }
            },
            // Invalidates all queries that subscribe to this Plot `id` only.
            // In this case, `getPlot` will be re-run. `getPlots` *might*  rerun, if this id was under its results.
            invalidatesTags: (result, error, { _id }) => error?[]:[{ type: 'Plots', id: result.plotId }, { type: 'Searches', id: 'ALL'}],
        }),
        updatePlotOccupant: builder.mutation({
            query(data) {
                const { _id, ...body } = data

                return {
                    url: `plot/occupant/${_id}`,
                    method: 'PUT',
                    body,
                }
            },
            // Invalidates all queries that subscribe to this Plot `id` only.
            // In this case, `getPlot` will be re-run. `getPlots` *might*  rerun, if this id was under its results.
            invalidatesTags: (result, error, { _id }) => error?[]:[{ type: 'Plots', id: result.plotId }, { type: 'Searches', id: 'ALL'}],
        }),
        deletePlotOccupant: builder.mutation({
            query(_id) {
                return {
                    url: `plot/occupant/${_id}`,
                    method: 'DELETE',
                }
            },
            // Invalidates all queries that subscribe to this Plot `id` only.
            invalidatesTags: (result, error, arg) => error?[]:[{ type: 'Plots', id: result.plotId }, { type: 'Searches', id: 'ALL'}],
        }),
        getPlotByLot: builder.query({
            query(data) {
                return {
                    url: `plot/find-lot/`,
                    method: 'POST',
                    body: data,
                }
            },
            providesTags: (result, error, { _id }) => error?[]:[{ type: 'Plots', id: _id }],
        }),
        searchPlots: builder.query({
            query(searchString) {
                return {
                    url: `plot/search/`,
                    method: 'POST',
                    body: { query: searchString },
                }
            },
            invalidatesTags: (result, error, args) => error?[]:[{ type: 'Searches', id: (args + '')}, { type: 'Searches', id: 'ALL'}],
        }),
    }),
})

export const {
    useGetPlotsQuery,
    useAddPlotMutation,
    useGetPlotQuery,
    useUpdatePlotMutation,
    useDeletePlotMutation,
    useAddPlotOwnerMutation,
    useUpdatePlotOwnerMutation,
    useDeletePlotOwnerMutation,
    useGetPlotByLotQuery,
    useAddPlotOccupantMutation,
    useUpdatePlotOccupantMutation,
    useDeletePlotOccupantMutation,
    useSearchPlotsQuery,
} = plotsApi